import { parseBuffer } from 'music-metadata';

export const pLimit = async (tasks, limit) => {
    const results   = new Array(tasks.length);
    const executing = new Set();

    for (let i = 0; i < tasks.length; i++) {
        const idx = i;
        const p = Promise.resolve()
            .then(() => tasks[idx]())
            .then(result => {
                executing.delete(p);
                results[idx] = result;
            });

        executing.add(p);
        if (executing.size >= limit) await Promise.race(executing);
    }

    await Promise.all(executing);
    return results;
};

export const pollWithBackoff = (pollFn, onStatusUpdate, isDone, onFinished) => {
    let attempts  = 0;
    let delay     = 4000;                   // initial delay ms
    const MAX_ATTEMPTS   = 20;              // ~4 min total at 30 s cap
    const MAX_DELAY_MS   = 30_000;          // ceiling between rounds
    const BACKOFF_FACTOR = 1.5;

    let timeoutId  = null;
    let cancelled  = false;
    let errorStreak = 0;

    const poll = async () => {
        if (cancelled) return;
        if (attempts >= MAX_ATTEMPTS) {
            console.warn('[pollWithBackoff] Timed out after max attempts');
            onFinished();
            return;
        }
        attempts++;

        try {
            const statuses = await pollFn();
            if (cancelled) return;

            errorStreak = 0;                // reset on success
            onStatusUpdate(statuses);

            if (isDone(statuses)) {
                onFinished();
                return;
            }
        } catch (err) {
            console.error('[pollWithBackoff] Poll error:', err);
            errorStreak++;
            if (errorStreak >= 3) {
                console.warn('[pollWithBackoff] Stopping after 3 consecutive errors');
                onFinished();
                return;
            }
        }

        delay     = Math.min(delay * BACKOFF_FACTOR, MAX_DELAY_MS);
        timeoutId = setTimeout(poll, delay);
    };

    timeoutId = setTimeout(poll, delay);

    return () => {
        cancelled = true;
        if (timeoutId) clearTimeout(timeoutId);
    };
};

export const getDurationFromUrl = async (sourceUrl, filename) => {
    let audioContext = null;
    try {
        const fetchRes    = await fetch(sourceUrl);
        const arrayBuffer = await fetchRes.arrayBuffer();
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const durationSecs = audioBuffer.duration;
        const minutes = Math.floor(durationSecs / 60);
        const seconds = Math.floor(durationSecs % 60);
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } catch (e) {
        console.warn(`Duration extract failed for: ${filename}`);
        return null;
    } finally {
        if (audioContext) audioContext.close();
    }
};

export const getDuration = async (file) => {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer); // Convert to Uint8Array for browser
        const metadata = await parseBuffer(uint8Array, file.type || file.name);
        const durationSecs = metadata.format.duration;
        return durationSecs;
    } catch (e) {
        console.warn(`Duration extract failed for: ${file.name}`, e);
        return null;
    }
};

// export const getDuration = async (file) => {
//     let audioContext = null;
//     try {
//         const arrayBuffer = await file.arrayBuffer();  
//         audioContext = new (window.AudioContext || window.webkitAudioContext)();
//         const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
//         const durationSecs = audioBuffer.duration;
//         // const minutes = Math.floor(durationSecs / 60);
//         // const seconds = Math.floor(durationSecs % 60);
//         return durationSecs;
//     } catch (e) {
//         console.warn(`Duration extract failed for: ${file.name}`);
//         return null;
//     } finally {
//         if (audioContext) audioContext.close();
//     }
// };

export const normalizeContentType = (contentType) => {
    const ct = (contentType || '').toLowerCase().trim();
    const wavTypes  = ['audio/wav', 'audio/wave', 'audio/x-wav', 'audio/vnd.wave', 'audio/x-pn-wav'];
    const mp3Types  = ['audio/mpeg', 'audio/mp3', 'audio/x-mpeg', 'audio/x-mp3', 'audio/mpeg3', 'audio/x-mpeg3'];
    const flacTypes = ['audio/flac', 'audio/x-flac'];
    const aacTypes  = ['audio/aac', 'audio/x-aac', 'audio/aacp'];
    const oggTypes  = ['audio/ogg', 'audio/x-ogg', 'audio/vorbis'];
    if (wavTypes.includes(ct))  return 'audio/wav';
    if (mp3Types.includes(ct))  return 'audio/mpeg';
    if (flacTypes.includes(ct)) return 'audio/flac';
    if (aacTypes.includes(ct))  return 'audio/aac';
    if (oggTypes.includes(ct))  return 'audio/ogg';
    return ct || 'audio/mpeg';
};